/**
 * BM-H3407 - one name line for a matter that has joint clients.
 *
 * Vanessa, 1 Oct 2026: "where a matter has joint clients, can all screens
 * please name both".
 *
 * Mirrors backend/hub/client_display_name.py:
 *   two clients    "Ann Smith & Bob Jones"
 *   three or more  "Ann Smith, Bob Jones & Cara Lee"
 *
 * Use this for what a screen SHOWS. Never feed the result back into
 * jointCoverage.js or a form field: client_name stays the primary client
 * alone, because the conflict coverage check reads it as one searched name.
 */

const norm = (v) => String(v == null ? "" : v).trim().toLowerCase().replace(/\s+/g, " ");
const tidy = (v) => String(v == null ? "" : v).trim().replace(/\s+/g, " ");

/** Names of the parties in a stored joint_clients array. */
export const jointNamesFrom = (parties) =>
  (Array.isArray(parties) ? parties : [])
    .map((p) => {
      if (typeof p === "string") return tidy(p);
      if (p && typeof p === "object") return tidy(p.full_name || p.name || p.client_name || "");
      return "";
    })
    .filter((n) => n.length > 0);

/** "A & B" / "A, B & C"; a joint name already in the primary line is not repeated. */
export const joinClientNames = (primary, joint = []) => {
  const first = tidy(primary);
  const line = norm(first);
  const names = first ? [first] : [];
  const seen = new Set(line ? [line] : []);
  for (const name of joint || []) {
    const n = norm(name);
    if (!n || seen.has(n) || (line && line.includes(n))) continue;
    seen.add(n);
    names.push(tidy(name));
  }
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
};

/**
 * The client line for any row a screen renders.
 *
 * Prefers the server's client_display_name; otherwise composes one from the
 * row's own primary name and joint_clients / joint_client_names; otherwise
 * the fallback passed in.
 */
/**
 * BM-H3474 (Liezel, 2 Oct 2026): the client on an onboarding record. A company
 * client is the company, not the director who filled in the OB form; the form
 * keeps them apart as company_name and full_name. Mirrors client_party_name in
 * backend/hub/client_display_name.py.
 */
export const clientPartyName = (rec) => {
  const pc = (rec && rec.primary_client) || {};
  const company = tidy(pc.company_name);
  const entity = String((rec && rec.entity_type) || "").trim().toLowerCase();
  if (company && entity !== "individual") return company;
  return tidy(pc.full_name || pc.name);
};

export const clientDisplayName = (row, fallback = "") => {
  if (!row || typeof row !== "object") return tidy(fallback);
  const primary =
    row.client_name ||
    clientPartyName(row) ||
    row.primary_label ||
    fallback;
  const joint = [
    ...jointNamesFrom(row.joint_clients),
    ...jointNamesFrom(row.joint_client_names),
  ];
  const server = tidy(row.client_display_name);
  if (server) return server;
  return joinClientNames(primary, joint) || tidy(fallback);
};

export default clientDisplayName;
